import { ProfileCity } from 'app/design/icons/profile-city';
import { ShareAndroid } from 'app/design/icons/shareAndroid';
import { Column, Row } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface Properties {
  open: boolean;
  onClose: () => void;
  onLeave: () => void;
  isLeaving: boolean;
}

export function LeavePlaceBottomSheet({ open, onClose, onLeave, isLeaving }: Properties) {
  const { t } = useTranslation();

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      showIndicator={false}
      CustomHeaderComponent={
        <Row
          alignItems="center"
          style={{
            borderTopLeftRadius: theme.borderRadii['radius.xl'],
            borderTopRightRadius: theme.borderRadii['radius.xl'],
            paddingVertical: theme.spacing['spacing.general.x4'],
            paddingHorizontal: theme.spacing['spacing.general.x5'],
            position: 'relative',
            justifyContent: 'center',
          }}
        >
          <Button
            accessibilityLabel={t('close', { ns: 'accessibility' })}
            hitSlop={20}
            onPress={onClose}
            variant="plain"
            style={{ position: 'absolute', left: theme.spacing['spacing.general.x2'] }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} size={22} color={theme.colors['text.primary']} />
          </Button>
          <Text fontWeight="700" variant="bodyLarge">
            {t('place.leaveTitle', { ns: 'neighbour-profile' })}
          </Text>
        </Row>
      }
    >
      <Column
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x5'],
          paddingBottom: theme.spacing['spacing.general.x12'],
        }}
      >
        {/* Info rows */}
        <Column
          style={{
            borderRadius: theme.borderRadii['radius.md'],
            borderWidth: 1,
            borderColor: theme.colors['surface.disabled'],
            overflow: 'hidden',
          }}
        >
          <Row alignItems="center" gap="spacing.general.x4" style={{ padding: theme.spacing['spacing.general.x3'] }}>
            <Column
              alignItems="center"
              justifyContent="center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: theme.colors['surface.tertiary-neutral'],
              }}
            >
              <ProfileCity color={theme.colors['text.secondary']} width={20} height={20} />
            </Column>
            <Text color="text.primary" variant="bodyBase" flex={1}>
              {t('place.leave.profileDesc', { ns: 'neighbour-profile' })}
            </Text>
          </Row>

          <View style={{ height: 1, backgroundColor: theme.colors['surface.disabled'] }} />

          <Row alignItems="center" gap="spacing.general.x4" style={{ padding: theme.spacing['spacing.general.x3'] }}>
            <Column
              alignItems="center"
              justifyContent="center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: theme.colors['surface.tertiary-neutral'],
              }}
            >
              <ShareAndroid color={theme.colors['text.secondary']} width={20} height={20} />
            </Column>
            <Text color="text.primary" variant="bodyBase" flex={1}>
              {t('place.leave.offersDesc', { ns: 'neighbour-profile' })}
            </Text>
          </Row>
        </Column>

        {/* Confirm */}
        <Button
          flex={1}
          justifyContent="center"
          loading={isLeaving}
          minHeight={48}
          onPress={onLeave}
          marginTop="spacing.general.x4"
          style={{ borderRadius: theme.borderRadii['radius.xl'] }}
          variant="primary"
        >
          <Text color="text.inverse.primary" fontWeight="700" textAlign="center" variant="bodyBase">
            {t('place.confirm', { ns: 'neighbour-profile' })}
          </Text>
        </Button>

        {/* Cancel */}
        <Button
          flex={1}
          justifyContent="center"
          minHeight={48}
          onPress={onClose}
          style={{
            borderRadius: theme.borderRadii['radius.xl'],
            borderWidth: 1.5,
            borderColor: theme.colors['surface.disabled'],
            marginTop: theme.spacing['spacing.general.x2'],
          }}
        >
          <Text fontWeight="600" textAlign="center" variant="bodyBase">
            {t('cancel', { ns: 'common' })}
          </Text>
        </Button>
      </Column>
    </BottomSheet>
  );
}
